import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { toast } from "react-toastify";
import { downloadVisaDocumentPdf, openVisaDocumentPdf } from "utils/visaDocumentService";
import AdminFillFieldsForm, {
  blockedRecordOwnedSignatures,
  missingRequiredAdminFields,
} from "./AdminFillFieldsForm";

const safeFileName = (title) => `${String(title || "document").replace(/[^\w.-]+/g, "_")}.pdf`;

const CompleteAdminDocumentDialog = ({
  open,
  onClose,
  documentTitle,
  fields = [],
  assetUrls = {},
  onSubmit,
  loading = false,
  visaId,
  templateId,
  canViewSubmission = false,
  uppercase = false,
}) => {
  const [values, setValues] = useState({});
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Seed each field's resolved default (the agent's declaration date, already set to today by the
  // server) so the admin sees what will be stamped instead of a blank box they must retype.
  useEffect(() => {
    if (!open) return;
    const defaults = {};
    fields.forEach((field) => {
      if (field.defaultValue) defaults[field.name] = field.defaultValue;
    });
    setValues(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const missing = missingRequiredAdminFields(fields, values, assetUrls);
  const blocked = blockedRecordOwnedSignatures(fields, values, assetUrls);
  const canSubmit = missing.length === 0 && blocked.length === 0;

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      await openVisaDocumentPdf(visaId, templateId);
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || "Failed to open the client's submission");
    } finally {
      setPreviewing(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadVisaDocumentPdf(visaId, templateId, safeFileName(documentTitle));
    } catch (err) {
      toast.error(
        err?.response?.data?.errorMessage || "Failed to download the client's submission"
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className="font-semibold">Complete "{documentTitle}"</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {fields.length > 0
              ? "The client has signed. Review their submission, then fill the remaining admin fields to finalize this document."
              : "The client has signed. Review their submission, then confirm to finalize this document."}
          </Typography>

          {canViewSubmission && (
            <>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                  onClick={handlePreview}
                  disabled={previewing}
                >
                  {previewing ? "Opening…" : "Preview client's submission"}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
                  onClick={handleDownload}
                  disabled={downloading}
                >
                  {downloading ? "Downloading…" : "Download"}
                </Button>
              </Stack>
              <Divider />
            </>
          )}

          {fields.length > 0 && (
            <AdminFillFieldsForm
              fields={fields}
              values={values}
              assetUrls={assetUrls}
              onChange={setValues}
              disabled={loading}
              uppercase={uppercase}
            />
          )}

          {blocked.length > 0 && (
            <Alert severity="warning">
              The MARA agent on this document has no {blocked.join(", ").toLowerCase()} on file.
              Someone with MARA access has to add it under Manage MARA before this document can be
              completed.
            </Alert>
          )}

          {missing.length > 0 && <Alert severity="info">Still needed: {missing.join(", ")}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => canSubmit && onSubmit?.({ fieldValues: values })}
          disabled={loading || !canSubmit}
        >
          {loading ? "Completing…" : "Complete document"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompleteAdminDocumentDialog;
